import {
    AccountBalance,
    Assessment,
    AutoAwesomeOutlined,
    BugReport,
    Business,
    Code,
    CreditCard,
    Email,
    Insights,
    LockOpen,
    MenuBook,
    People,
    Security,
    SubdirectoryArrowRight,
    TrendingUp,
} from "@mui/icons-material";
import React, { lazy } from "react";
import { matchPath } from "react-router-dom";

import { LEGACY_ADMIN_ROUTES } from "./adminLegacyRoutes";
import type { AdminPermissionKey } from "./adminPermissionKeys";
import type { AdminV2RevenueMenuVisibilityKey } from "@/types/adminV2";
import { isRevenueMenuVisibilityEnabled } from "@/utils/revenueAccessVisibility";

/**
 * A single source of truth for:
 * - route paths + human labels (used by `Sidebar` and breadcrumbs)
 * - sidebar information architecture (grouping + ordering)
 * - lazy page boundaries (reduces initial bundle size)
 *
 * @remarks
 * This module intentionally avoids importing the concrete page modules directly.
 * Instead, routes use `React.lazy(() => import(...))` so:
 * - the route table remains central and stable
 * - pages are code-split and loaded on demand
 * - we avoid dependency cycles between `PageLayout` (breadcrumbs) and pages
 */

export type AdminNavGroupId =
    | "people"
    | "growth"
    | "sales"
    | "product"
    | "support"
    | "learning"
    | "revenue"
    | "insights"
    | "settings"
    | "advanced"
    | "control_plane_v2"
    | "settings_v2"
    | "users_orgs"
    | "billing"
    | "email"
    | "integrations"
    | "impersonation"
    | "analytics"
    | "observability"
    | "dev_tools"
    | "admin";

export interface AdminNavGroup {
    id: AdminNavGroupId;
    label: string;
    icon: React.ReactNode;
    defaultExpanded?: boolean;
}

export interface AdminRouteDefinition {
    id: string;
    /**
     * React Router path pattern (we use absolute paths for consistency).
     */
    path: string;
    /**
     * Human-friendly label used in sidebar + breadcrumbs.
     */
    label: string;
    icon: React.ReactNode;
    /**
     * `null` means this is a top-level route (e.g. Dashboard).
     */
    navGroup: AdminNavGroupId | null;
    /**
     * Lower numbers appear earlier within the same group.
     */
    navOrder: number;
    /**
     * Hidden routes are still reachable (deep links / internal navigation) but
     * do not appear in the sidebar.
     */
    navHidden?: boolean;
    /**
     * Lazy-loaded route page component.
     */
    Component: React.LazyExoticComponent<React.ComponentType>;
    /**
     * Optional RBAC gate for sidebar visibility.
     */
    requiredPermission?: AdminPermissionKey;
    /**
     * Optional menu visibility gate resolved from `/admin/v2/revenue/access-context`.
     */
    requiredMenuVisibility?: AdminV2RevenueMenuVisibilityKey;
}

export interface SidebarNavigationItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    path?: string;
    badge?: number;
    children?: SidebarNavigationItem[];
    disabled?: boolean;
}

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
    {
        id: "users_orgs",
        label: "Users & Organisations",
        icon: <People />,
        defaultExpanded: true,
    },
    {
        id: "billing",
        label: "Billing",
        icon: <CreditCard />,
        defaultExpanded: true,
    },
    {
        id: "email",
        label: "Email",
        icon: <Email />,
    },
    {
        id: "integrations",
        label: "Integrations",
        icon: <AccountBalance />,
    },
    {
        id: "support",
        label: "Support",
        icon: <People />,
    },
    {
        id: "observability",
        label: "Observability",
        icon: <BugReport />,
        defaultExpanded: true,
    },
    {
        id: "analytics",
        label: "Analytics",
        icon: <Insights />,
    },
    {
        id: "settings",
        label: "Settings",
        icon: <Security />,
    },
    {
        id: "admin",
        label: "Admin",
        icon: <Security />,
    },
    {
        id: "dev_tools",
        label: "Docs & Dev Tools",
        icon: <MenuBook />,
    },
    {
        id: "control_plane_v2",
        label: "Admin V2 Preview",
        icon: <Code />,
    },
];

const RELEASE_SURFACE_NAV_GROUP_IDS = new Set<AdminNavGroupId>([
    "users_orgs",
    "billing",
    "email",
    "integrations",
    "support",
    "settings",
    "analytics",
    "observability",
    "dev_tools",
    "admin",
]);

export const ADMIN_ROUTES: AdminRouteDefinition[] = [
    {
        id: "dashboard_entry",
        path: "/",
        label: "Dashboard",
        icon: <Insights />,
        navGroup: null,
        navOrder: 0,
        navHidden: true,
        Component: lazy(
            () => import("@/pages/AdminV2/Dashboard/AdminDashboardEntryPage"),
        ),
    },
    {
        id: "v2_crm",
        path: "/v2/crm",
        label: "People",
        icon: <Business />,
        navGroup: "people",
        navOrder: 10,
        requiredPermission: "crm.view",
        Component: lazy(
            () => import("@/pages/AdminV2/CRM/CrmContactsListPage"),
        ),
    },
    {
        id: "v2_crm_contacts_import",
        path: "/v2/crm/contacts/import",
        label: "Contacts Import",
        icon: <Business />,
        navGroup: "people",
        navOrder: 10.5,
        navHidden: true,
        requiredPermission: "crm.edit",
        Component: lazy(
            () => import("@/pages/AdminV2/CRM/CrmContactsImportPage"),
        ),
    },
    {
        id: "v2_crm_contact_record",
        path: "/v2/crm/contacts/:contactId",
        label: "Contact",
        icon: <Business />,
        navGroup: "people",
        navOrder: 11,
        navHidden: true,
        requiredPermission: "crm.view",
        Component: lazy(
            () => import("@/pages/AdminV2/CRM/CrmContactRecordPage"),
        ),
    },
    {
        id: "v2_crm_businesses",
        path: "/v2/crm/businesses",
        label: "Businesses",
        icon: <Business />,
        navGroup: "people",
        navOrder: 12,
        navHidden: false,
        requiredPermission: "crm.view",
        Component: lazy(
            () => import("@/pages/AdminV2/CRM/CrmBusinessesListPage"),
        ),
    },
    {
        id: "v2_crm_business_record",
        path: "/v2/crm/businesses/:businessId",
        label: "Business",
        icon: <Business />,
        navGroup: "people",
        navOrder: 13,
        navHidden: true,
        requiredPermission: "crm.view",
        Component: lazy(
            () => import("@/pages/AdminV2/CRM/CrmBusinessRecordPage"),
        ),
    },
    {
        id: "v2_crm_firms",
        path: "/v2/crm/firms",
        label: "Firms",
        icon: <Business />,
        navGroup: "people",
        navOrder: 14,
        navHidden: false,
        requiredPermission: "crm.view",
        Component: lazy(() => import("@/pages/AdminV2/CRM/CrmFirmsListPage")),
    },
    {
        id: "v2_crm_firm_record",
        path: "/v2/crm/firms/:firmId",
        label: "Firm",
        icon: <Business />,
        navGroup: "people",
        navOrder: 15,
        navHidden: true,
        requiredPermission: "crm.view",
        Component: lazy(() => import("@/pages/AdminV2/CRM/CrmFirmRecordPage")),
    },
    {
        id: "v2_crm_firm_clients",
        path: "/v2/crm/firm-clients",
        label: "Firm Clients",
        icon: <Business />,
        navGroup: "people",
        navOrder: 16,
        navHidden: false,
        requiredPermission: "crm.view",
        Component: lazy(
            () => import("@/pages/AdminV2/CRM/CrmFirmClientsListPage"),
        ),
    },
    {
        id: "v2_crm_firm_client_record",
        path: "/v2/crm/firm-clients/:relationshipId",
        label: "Firm Client Relationship",
        icon: <Business />,
        navGroup: "people",
        navOrder: 17,
        navHidden: true,
        requiredPermission: "crm.view",
        Component: lazy(
            () => import("@/pages/AdminV2/CRM/CrmFirmClientRecordPage"),
        ),
    },
    {
        id: "v2_growth",
        path: "/v2/growth",
        label: "Forms",
        icon: <TrendingUp />,
        navGroup: "growth",
        navOrder: 20,
        requiredPermission: "forms.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Growth/GrowthFormsListPage"),
        ),
    },
    {
        id: "v2_growth_form_builder",
        path: "/v2/growth/forms/:formId",
        label: "Form Builder",
        icon: <TrendingUp />,
        navGroup: "growth",
        navOrder: 20.1,
        navHidden: true,
        requiredPermission: "forms.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Growth/GrowthFormBuilderPage"),
        ),
    },
    {
        id: "v2_growth_tags",
        path: "/v2/growth/tags",
        label: "Tags",
        icon: <TrendingUp />,
        navGroup: "growth",
        navOrder: 20.2,
        navHidden: false,
        requiredPermission: "tags.manage",
        Component: lazy(() => import("@/pages/AdminV2/Growth/GrowthTagsPage")),
    },
    {
        id: "v2_growth_segments",
        path: "/v2/growth/segments",
        label: "Segments",
        icon: <TrendingUp />,
        navGroup: "growth",
        navOrder: 20.3,
        navHidden: false,
        requiredPermission: "segments.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Growth/GrowthSegmentsPage"),
        ),
    },
    {
        id: "v2_growth_campaigns",
        path: "/v2/growth/campaigns",
        label: "Campaigns",
        icon: <TrendingUp />,
        navGroup: "growth",
        navOrder: 20.4,
        navHidden: false,
        requiredPermission: "messaging.view",
        Component: lazy(
            () => import("@/pages/AdminV2/Growth/GrowthCampaignsPage"),
        ),
    },
    {
        id: "v2_growth_campaign_metrics",
        path: "/v2/growth/campaigns/:campaignId",
        label: "Campaign Metrics",
        icon: <TrendingUp />,
        navGroup: "growth",
        navOrder: 20.5,
        navHidden: true,
        requiredPermission: "messaging.view",
        Component: lazy(
            () => import("@/pages/AdminV2/Growth/GrowthCampaignMetricsPage"),
        ),
    },
    {
        id: "v2_growth_sequences",
        path: "/v2/growth/sequences",
        label: "Sequences",
        icon: <TrendingUp />,
        navGroup: "growth",
        navOrder: 20.6,
        navHidden: false,
        requiredPermission: "messaging.manage_sequences",
        Component: lazy(
            () => import("@/pages/AdminV2/Growth/GrowthSequencesPage"),
        ),
    },
    {
        id: "v2_dashboard",
        path: "/v2/dashboard",
        label: "Dashboard",
        icon: <Assessment />,
        navGroup: null,
        navOrder: 1,
        Component: lazy(
            () => import("@/pages/AdminV2/Dashboard/AdminV2DashboardPage"),
        ),
    },
    {
        id: "v2_support",
        path: "/v2/support",
        label: "Tickets",
        icon: <People />,
        navGroup: "support",
        navOrder: 30,
        requiredPermission: "support.view",
        Component: lazy(
            () => import("@/pages/AdminV2/Support/SupportTicketsListPage"),
        ),
    },
    {
        id: "v2_support_ticket",
        path: "/v2/support/tickets/:ticketId",
        label: "Support Ticket",
        icon: <People />,
        navGroup: "support",
        navOrder: 30.1,
        navHidden: true,
        requiredPermission: "support.view",
        Component: lazy(
            () => import("@/pages/AdminV2/Support/SupportTicketDetailPage"),
        ),
    },
    {
        id: "v2_support_help_centre",
        path: "/v2/support/help-centre",
        label: "Help Centre",
        icon: <MenuBook />,
        navGroup: "support",
        navOrder: 30.2,
        navHidden: false,
        requiredPermission: "kb.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Support/SupportHelpCentrePage"),
        ),
    },
    {
        id: "v2_support_help_centre_article",
        path: "/v2/support/help-centre/articles/:articleId",
        label: "Help Article",
        icon: <MenuBook />,
        navGroup: "support",
        navOrder: 30.3,
        navHidden: true,
        requiredPermission: "kb.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Support/SupportHelpCentrePage"),
        ),
    },
    {
        id: "v2_messaging",
        path: "/v2/messaging",
        label: "Communication identities",
        icon: <Email />,
        navGroup: "settings",
        navOrder: 50,
        requiredPermission: "messaging.view",
        Component: lazy(
            () =>
                import(
                    "@/pages/AdminV2/Messaging/MessagingSenderIdentitiesPage"
                ),
        ),
    },
    {
        id: "v2_messaging_templates",
        path: "/v2/messaging/templates",
        label: "Templates",
        icon: <Email />,
        navGroup: "growth",
        navOrder: 20.7,
        navHidden: false,
        requiredPermission: "messaging.view",
        Component: lazy(
            () => import("@/pages/AdminV2/Messaging/MessagingTemplatesPage"),
        ),
    },
    {
        id: "v2_automations",
        path: "/v2/automations",
        label: "Automations",
        icon: <SubdirectoryArrowRight />,
        navGroup: "growth",
        navOrder: 20.8,
        requiredPermission: "automation.view",
        Component: lazy(() => import("@/pages/AdminV2/Growth/AutomationsPage")),
    },
    {
        id: "v2_product",
        path: "/v2/product",
        label: "Overview",
        icon: <Assessment />,
        navGroup: "product",
        navOrder: 10,
        requiredPermission: "featureflags.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Product/ProductWorkspacePage"),
        ),
    },
    {
        id: "v2_revenue",
        path: "/v2/revenue",
        label: "Offers & onboarding",
        icon: <CreditCard />,
        navGroup: "revenue",
        navOrder: 60,
        requiredPermission: "billing.view",
        requiredMenuVisibility: "revenue",
        Component: lazy(
            () => import("@/pages/AdminV2/Revenue/RevenueOffersOnboardingPage"),
        ),
    },
    {
        id: "v2_revenue_billing_dashboards",
        path: "/v2/revenue/billing",
        label: "Billing dashboards",
        icon: <CreditCard />,
        navGroup: "revenue",
        navOrder: 60.1,
        navHidden: false,
        requiredPermission: "billing.view",
        requiredMenuVisibility: "revenue",
        Component: lazy(
            () =>
                import("@/pages/AdminV2/Revenue/RevenueBillingDashboardsPage"),
        ),
    },
    {
        id: "v2_revenue_coupons",
        path: "/v2/revenue/coupons",
        label: "Coupons",
        icon: <CreditCard />,
        navGroup: "revenue",
        navOrder: 60.2,
        navHidden: false,
        requiredPermission: "billing.view",
        requiredMenuVisibility: "revenue",
        Component: lazy(
            () => import("@/pages/AdminV2/Revenue/RevenueCouponsPage"),
        ),
    },
    {
        id: "v2_revenue_affiliates",
        path: "/v2/revenue/affiliates",
        label: "Affiliates",
        icon: <CreditCard />,
        navGroup: "revenue",
        navOrder: 60.3,
        navHidden: false,
        requiredPermission: "billing.view",
        requiredMenuVisibility: "revenue",
        Component: lazy(
            () => import("@/pages/AdminV2/Revenue/RevenueAffiliatesPage"),
        ),
    },
    {
        id: "v2_settings_webhook_logs",
        path: "/v2/settings/webhook-logs",
        label: "Webhook logs",
        icon: <Assessment />,
        navGroup: "settings",
        navOrder: 48,
        requiredPermission: "billing.view",
        requiredMenuVisibility: "revenue",
        Component: lazy(
            () => import("@/pages/AdminV2/Revenue/RevenueBillingDashboardsPage"),
        ),
    },
    {
        id: "v2_learning",
        path: "/v2/learning",
        label: "Scores & pathways",
        icon: <MenuBook />,
        navGroup: "learning",
        navOrder: 70,
        requiredPermission: "learning.view",
        requiredMenuVisibility: "learning",
        Component: lazy(
            () => import("@/pages/AdminV2/Learning/LearningWorkspacePage"),
        ),
    },
    {
        id: "v2_sales",
        path: "/v2/sales",
        label: "Pipeline & work queues",
        icon: <People />,
        navGroup: "sales",
        navOrder: 80,
        requiredPermission: "crm.view",
        requiredMenuVisibility: "sales",
        Component: lazy(
            () => import("@/pages/AdminV2/Sales/SalesWorkspacePage"),
        ),
    },
    {
        id: "v2_insights_integrations",
        path: "/v2/insights-integrations",
        label: "Insights",
        icon: <Insights />,
        navGroup: "insights",
        navOrder: 90,
        requiredPermission: "analytics.view",
        Component: lazy(
            () =>
                import(
                    "@/pages/AdminV2/InsightsIntegrations/InsightsIntegrationsWorkspacePage"
                ),
        ),
    },
    {
        id: "v2_team_permissions",
        path: "/v2/settings/team-permissions",
        label: "Team & Permissions",
        icon: <Security />,
        navGroup: "settings",
        navOrder: 10,
        requiredPermission: "rbac.manage",
        Component: lazy(() => import("@/pages/AdminV2/TeamPermissionsPage")),
    },
    {
        id: "v2_approvals",
        path: "/v2/settings/approvals",
        label: "Approvals Queue",
        icon: <LockOpen />,
        navGroup: "settings",
        navOrder: 20,
        requiredPermission: "approvals.view",
        Component: lazy(() => import("@/pages/AdminV2/ApprovalsQueuePage")),
    },
    {
        id: "v2_audit_log",
        path: "/v2/settings/audit-log",
        label: "Audit Log",
        icon: <Assessment />,
        navGroup: "settings",
        navOrder: 30,
        requiredPermission: "audit.view",
        Component: lazy(() => import("@/pages/AdminV2/AuditLogPage")),
    },
    {
        id: "v2_jobs_monitor",
        path: "/v2/settings/jobs",
        label: "Jobs Monitor",
        icon: <TrendingUp />,
        navGroup: "settings",
        navOrder: 40,
        requiredPermission: "jobs.view",
        Component: lazy(() => import("@/pages/AdminV2/JobsMonitorPage")),
    },
    {
        id: "v2_ai_insights_access",
        path: "/v2/settings/ai-insights",
        label: "AI Insights Access",
        icon: <AutoAwesomeOutlined />,
        navGroup: "product",
        navOrder: 44.5,
        requiredPermission: "entitlements.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Settings/AiInsightsAccessPage"),
        ),
    },
    {
        id: "v2_feature_flags",
        path: "/v2/settings/feature-flags",
        label: "Feature Flags",
        icon: <Security />,
        navGroup: "product",
        navOrder: 45,
        requiredPermission: "featureflags.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Settings/FeatureFlagsPage"),
        ),
    },
    {
        id: "v2_entitlements",
        path: "/v2/settings/entitlements",
        label: "Entitlements",
        icon: <Security />,
        navGroup: "product",
        navOrder: 46,
        requiredPermission: "entitlements.manage",
        Component: lazy(
            () => import("@/pages/AdminV2/Settings/EntitlementsPage"),
        ),
    },
    {
        id: "v2_settings_integrations",
        path: "/v2/settings/integrations",
        label: "Integrations",
        icon: <AccountBalance />,
        navGroup: "settings",
        navOrder: 47,
        requiredPermission: "integrations.view",
        Component: lazy(
            () => import("@/pages/AdminV2/Settings/IntegrationsSettingsPage"),
        ),
    },
    ...LEGACY_ADMIN_ROUTES,
];

export const isAdminV2Route = (route: AdminRouteDefinition): boolean => {
    return route.path.startsWith("/v2/");
};

const toKebabTitle = (value: string): string => {
    const normalized = value.replace(/[-_]+/g, " ").trim();
    return normalized.length === 0
        ? value
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/**
 * Builds a breadcrumb list for the given pathname.
 *
 * @remarks
 * This prefers route labels from `ADMIN_ROUTES` (including pattern matches like
 * `/EditableContentBlock/:contentId`) and falls back to a simple kebab-to-title
 * transform for unknown segments.
 */
export const getBreadcrumbsForPathname = (
    pathname: string,
): Array<{ label: string; path?: string }> => {
    const rootRoute = ADMIN_ROUTES.find(route => route.path === "/");
    const crumbs: Array<{ label: string; path?: string }> = [
        { label: rootRoute?.label ?? "Home", path: "/" },
    ];

    const segments = pathname.split("/").filter(Boolean);
    const prefixes = segments.map((_, index) => {
        return "/" + segments.slice(0, index + 1).join("/");
    });

    for (const prefix of prefixes) {
        if (prefix === "/") continue;

        const matchingRoute = ADMIN_ROUTES.find(route =>
            matchPath({ path: route.path, end: true }, prefix),
        );

        if (matchingRoute) {
            crumbs.push({
                label: matchingRoute.label,
                path: prefix,
            });
            continue;
        }

        const segment = prefix.split("/").filter(Boolean).slice(-1)[0];
        crumbs.push({ label: toKebabTitle(segment), path: prefix });
    }

    return crumbs;
};

/**
 * Builds the sidebar navigation tree from the route table and group definitions.
 */
export const getSidebarNavigationItems = (args?: {
    adminV2FoundationsEnabled?: boolean;
    permissionKeys?: ReadonlySet<string>;
    menuVisibility?: Partial<Record<AdminV2RevenueMenuVisibilityKey, boolean>>;
}): SidebarNavigationItem[] => {
    const resolveSidebarGroupId = (
        route: AdminRouteDefinition,
    ): AdminNavGroupId | null => {
        const navGroup = route.navGroup;
        if (!navGroup) {
            return null;
        }

        // Keep release-branch navigation centered on legacy/admin workflows.
        // Admin V2 routes stay reachable via deep links and a collapsed preview section.
        if (isAdminV2Route(route)) {
            return "control_plane_v2";
        }

        if (RELEASE_SURFACE_NAV_GROUP_IDS.has(navGroup)) {
            return navGroup;
        }

        return "admin";
    };

    const canShowRoute = (route: AdminRouteDefinition): boolean => {
        if (isAdminV2Route(route) && !args?.adminV2FoundationsEnabled) {
            return false;
        }

        if (!route.requiredPermission) {
            if (!route.requiredMenuVisibility) {
                return true;
            }
        } else {
            if (!args?.permissionKeys) {
                return false;
            }

            if (!args.permissionKeys.has(route.requiredPermission)) {
                return false;
            }
        }

        if (!route.requiredMenuVisibility) {
            return true;
        }

        return isRevenueMenuVisibilityEnabled(
            args?.menuVisibility,
            route.requiredMenuVisibility,
        );
    };

    const routeByGroup = new Map<AdminNavGroupId, AdminRouteDefinition[]>();

    for (const route of ADMIN_ROUTES) {
        const sidebarGroupId = resolveSidebarGroupId(route);

        if (!sidebarGroupId || route.navHidden || !canShowRoute(route)) {
            continue;
        }

        const existing = routeByGroup.get(sidebarGroupId) ?? [];
        existing.push(route);
        routeByGroup.set(sidebarGroupId, existing);
    }

    const bugTrackerRecentRoute = ADMIN_ROUTES.find(
        route => route.id === "bug_tracker_recent",
    );
    const featureRequestsRecentRoute = ADMIN_ROUTES.find(
        route => route.id === "feature_requests_recent",
    );
    const items: SidebarNavigationItem[] = [];
    const topLevelRoutes = ADMIN_ROUTES.filter(route => {
        return (
            !route.navGroup &&
            !route.navHidden &&
            canShowRoute(route) &&
            route.path !== "/"
        );
    }).sort((a, b) => a.navOrder - b.navOrder);

    items.push(
        ...topLevelRoutes.map(route => ({
            id: route.id,
            label: route.label,
            icon: route.icon,
            path: route.path,
        })),
    );

    for (const group of ADMIN_NAV_GROUPS) {
        const groupRoutes = (routeByGroup.get(group.id) ?? []).sort(
            (a, b) => a.navOrder - b.navOrder,
        );

        if (groupRoutes.length === 0) {
            continue;
        }

        items.push({
            id: group.id,
            label: group.label,
            icon: group.icon,
            children: groupRoutes.map(route => ({
                id: route.id,
                label: route.label,
                icon: route.icon,
                path: route.path,
                ...(route.id === "bug_tracker" && bugTrackerRecentRoute
                    ? {
                          children: [
                              {
                                  id: bugTrackerRecentRoute.id,
                                  label: bugTrackerRecentRoute.label,
                                  icon: bugTrackerRecentRoute.icon,
                                  path: bugTrackerRecentRoute.path,
                              },
                          ],
                      }
                    : route.id === "feature_requests" &&
                        featureRequestsRecentRoute
                      ? {
                            children: [
                                {
                                    id: featureRequestsRecentRoute.id,
                                    label: featureRequestsRecentRoute.label,
                                    icon: featureRequestsRecentRoute.icon,
                                    path: featureRequestsRecentRoute.path,
                                },
                            ],
                        }
                    : {}),
            })),
        });
    }

    return items;
};
